import React, { useEffect, useRef, useState } from 'react';
import { getCosts, createCost, deleteCost } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle, CheckCircle2, Eye, Paperclip, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { CATEGORIES, CATEGORY_LABEL, Kpi, MIN_MARGIN_PCT, fmtEuro } from './shared';
import { CostInvoicePreview } from './CostInvoicePreview';
import { BudgetVsRealiseWidget } from './BudgetVsRealiseWidget';

/**
 * Generic budget/charges tab.
 * @param {string} categorie - "budget" (prévisionnel) or "charges" (réel).
 *
 * Behavior:
 * - "budget": pas de n° facture, document attendu = "Devis fournisseur" (optionnel),
 *             représente la prévision/estimation du commercial.
 * - "charges": comportement historique — n° de facture + facture fournisseur obligatoire.
 */
export const CostTab = ({ dossier, onChange, categorie = 'charges' }) => {
    const dossierId = dossier.id;
    const isBudget = categorie === 'budget';
    const caDevis = Number(dossier.montant_devis || 0);

    const [costs, setCosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewCost, setPreviewCost] = useState(null);
    const [bvrKey, setBvrKey] = useState(0);
    const fileInputRef = useRef(null);
    const [form, setForm] = useState({
        description: '',
        type_cout: 'materiau',
        montant: '',
        fournisseur: '',
        date_commande: format(new Date(), 'yyyy-MM-dd'),
        numero_commande: ''
    });
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await getCosts(dossierId, categorie);
            setCosts(res.data || []);
        } catch {
            setCosts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [dossierId, categorie]);

    const totalCosts = costs.reduce((s, c) => s + Number(c.montant || 0), 0);
    const margeBrute = caDevis - totalCosts;
    const margePct = caDevis > 0 ? (margeBrute / caDevis) * 100 : 0;
    const budgetProgress = caDevis > 0 ? Math.min((totalCosts / caDevis) * 100, 100) : 0;
    const marginOk = margePct >= MIN_MARGIN_PCT;

    const handleAdd = async () => {
        if (!form.description || !form.montant) {
            toast.error('Description et montant requis');
            return;
        }
        if (!isBudget && !file) {
            toast.error('La facture fournisseur est obligatoire');
            return;
        }
        setSubmitting(true);
        try {
            await createCost({
                dossier_id: dossierId,
                description: form.description,
                type_cout: form.type_cout,
                montant: Number(form.montant),
                fournisseur: form.fournisseur,
                date_commande: form.date_commande,
                numero_commande: isBudget ? '' : form.numero_commande,
                categorie
            }, file);
            setForm({
                description: '',
                type_cout: 'materiau',
                montant: '',
                fournisseur: '',
                date_commande: format(new Date(), 'yyyy-MM-dd'),
                numero_commande: ''
            });
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            await load();
            setBvrKey((k) => k + 1);
            onChange && onChange();
            toast.success(isBudget ? 'Ligne budgétaire ajoutée' : 'Ligne charges ajoutée');
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'ajout");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette ligne ?')) return;
        try {
            await deleteCost(id);
            await load();
            setBvrKey((k) => k + 1);
            onChange && onChange();
        } catch {
            toast.error('Erreur');
        }
    };

    // KPI labels adapt to the category
    const labels = isBudget
        ? {
            ca: 'CA devis HT',
            engaged: 'Budget prévisionnel HT',
            marge: 'Marge prévisionnelle HT',
            margePct: 'Marge prévisionnelle %',
            progress: 'Consommation du budget prévisionnel',
            addTitle: 'Ajouter une ligne prévisionnelle',
            fileLabel: 'Devis fournisseur (PDF/image) — optionnel',
            fileHelp: 'Joignez un devis fournisseur pour préparer votre chantier',
            addBtn: 'Ajouter la ligne',
            listTitle: 'Lignes prévisionnelles',
            empty: 'Aucune ligne budgétaire prévisionnelle',
        }
        : {
            ca: 'CA devis HT',
            engaged: 'Coûts engagés HT',
            marge: 'Marge brute HT',
            margePct: 'Marge %',
            progress: 'Consommation du budget',
            addTitle: 'Ajouter un coût',
            fileLabel: 'Facture fournisseur (PDF/image) *',
            fileHelp: 'La facture fournisseur est obligatoire',
            addBtn: 'Ajouter le coût',
            listTitle: 'Lignes de charges',
            empty: 'Aucun coût enregistré',
        };

    const testIdPrefix = isBudget ? 'budget' : 'charges';

    return (
        <div className="bg-white border border-[#E4E4E7] p-6 rounded-sm space-y-6" data-testid={`${testIdPrefix}-tab`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Kpi label={labels.ca} value={`${fmtEuro(caDevis)} €`} />
                <Kpi label={labels.engaged} value={`${fmtEuro(totalCosts)} €`} />
                <Kpi label={labels.marge} value={`${fmtEuro(margeBrute)} €`} tone={marginOk ? 'ok' : 'warn'} />
                <Kpi
                    label={labels.margePct}
                    value={`${margePct.toFixed(1)} %`}
                    tone={marginOk ? 'ok' : 'warn'}
                    icon={marginOk ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                />
            </div>

            <div>
                <div className="flex justify-between items-center text-sm mb-1">
                    <span className="font-medium text-[#52525B]">{labels.progress}</span>
                    <span className={`font-semibold ${budgetProgress > 100 ? 'text-red-600' : 'text-[#0A0A0A]'}`}>
                        {budgetProgress.toFixed(0)}%
                    </span>
                </div>
                <div className="w-full bg-[#F4F4F5] h-3 rounded-full overflow-hidden">
                    <div
                        className={`h-full transition-all ${budgetProgress >= 100 ? 'bg-red-500' : budgetProgress > 80 ? 'bg-orange-500' : 'bg-[#16A34A]'}`}
                        style={{ width: `${Math.min(budgetProgress, 100)}%` }}
                    />
                </div>
            </div>

            {caDevis > 0 && !marginOk && !isBudget && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-sm" data-testid="margin-alert">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1 text-sm">
                        <p className="font-semibold text-red-700">Marge sous le seuil minimum ({MIN_MARGIN_PCT}%)</p>
                        <p className="text-red-700/80">
                            Marge actuelle : <b>{margePct.toFixed(1)}%</b>. Vérifiez les coûts engagés ou ajustez le prix du devis.
                        </p>
                    </div>
                </div>
            )}

            <BudgetVsRealiseWidget dossierId={dossierId} refreshKey={bvrKey} />

            <div className="pt-4 border-t border-[#E4E4E7]">
                <h3 className="font-semibold mb-3">{labels.addTitle}</h3>
                <p className="text-xs text-[#52525B] mb-3 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {labels.fileHelp}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs text-[#52525B] uppercase">Description *</Label>
                        <Input
                            placeholder="Ex: Peinture murale RAL 9010"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="h-10 rounded-sm"
                            data-testid={`${testIdPrefix}-description-input`}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs text-[#52525B] uppercase">Catégorie</Label>
                        <select
                            className="h-10 w-full rounded-sm border border-[#E4E4E7] px-3 text-sm bg-white"
                            value={form.type_cout}
                            onChange={(e) => setForm({ ...form, type_cout: e.target.value })}
                            data-testid={`${testIdPrefix}-categorie-select`}
                        >
                            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs text-[#52525B] uppercase">Montant (€ HT) *</Label>
                        <Input
                            type="number"
                            step="0.01"
                            placeholder="Montant HT"
                            value={form.montant}
                            onChange={(e) => setForm({ ...form, montant: e.target.value })}
                            className="h-10 rounded-sm"
                            data-testid={`${testIdPrefix}-montant-input`}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs text-[#52525B] uppercase">Fournisseur</Label>
                        <Input
                            placeholder="Nom fournisseur"
                            value={form.fournisseur}
                            onChange={(e) => setForm({ ...form, fournisseur: e.target.value })}
                            className="h-10 rounded-sm"
                            data-testid={`${testIdPrefix}-fournisseur-input`}
                        />
                    </div>
                    {!isBudget && (
                        <div className="space-y-1">
                            <Label className="text-xs text-[#52525B] uppercase">N° facture / BC</Label>
                            <Input
                                placeholder="N° BC ou facture"
                                value={form.numero_commande}
                                onChange={(e) => setForm({ ...form, numero_commande: e.target.value })}
                                className="h-10 rounded-sm"
                            />
                        </div>
                    )}
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div className="md:col-span-2 space-y-1">
                        <Label className="text-xs text-[#52525B] uppercase">{labels.fileLabel}</Label>
                        <Input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,image/*"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="h-10 rounded-sm file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-[#F4F4F5] file:text-[#0A0A0A] file:cursor-pointer"
                            data-testid={`${testIdPrefix}-file-input`}
                        />
                        {file && (
                            <p className="text-xs text-[#16A34A]">
                                <Paperclip className="w-3 h-3 inline mr-1" />
                                {file.name} ({(file.size / 1024).toFixed(1)} Ko)
                            </p>
                        )}
                    </div>
                    <Button
                        onClick={handleAdd}
                        disabled={submitting}
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm h-10"
                        data-testid={`add-${testIdPrefix}-btn`}
                    >
                        <Plus className="w-4 h-4 mr-1" /> {submitting ? 'Ajout...' : labels.addBtn}
                    </Button>
                </div>
            </div>

            <div>
                <h3 className="font-semibold mb-3">{labels.listTitle} ({costs.length})</h3>
                {loading ? (
                    <p className="text-sm text-[#52525B]">Chargement...</p>
                ) : costs.length === 0 ? (
                    <p className="text-sm text-[#52525B] italic">{labels.empty}</p>
                ) : (
                    <div className="divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-sm">
                        {costs.map((c) => (
                            <div key={c.id} className="flex items-center justify-between p-3 text-sm gap-3" data-testid={`${testIdPrefix}-row-${c.id}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{c.description}</p>
                                    <p className="text-xs text-[#52525B]">
                                        {CATEGORY_LABEL[c.type_cout] || c.type_cout}
                                        {c.fournisseur && ` · ${c.fournisseur}`}
                                        {!isBudget && c.numero_commande && ` · ${c.numero_commande}`}
                                    </p>
                                </div>
                                <div className="font-semibold w-32 text-right shrink-0">{fmtEuro(c.montant)} € HT</div>
                                {c.invoice_path && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setPreviewCost(c)}
                                        className="rounded-sm h-8 w-8"
                                        title={isBudget ? 'Voir le devis fournisseur' : 'Voir la facture fournisseur'}
                                        data-testid={`view-${testIdPrefix}-doc-${c.id}`}
                                    >
                                        <Eye className="w-4 h-4 text-[#0284C7]" />
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDelete(c.id)}
                                    className="rounded-sm h-8 w-8"
                                >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                            </div>
                        ))}
                        <div className="flex items-center justify-between p-3 bg-[#FAFAFA] text-sm font-bold">
                            <span>Total</span>
                            <span className="w-32 text-right">{fmtEuro(totalCosts)} € HT</span>
                            <div className="w-8" />
                            <div className="w-8" />
                        </div>
                    </div>
                )}
            </div>

            <CostInvoicePreview
                cost={previewCost}
                open={!!previewCost}
                onOpenChange={(o) => !o && setPreviewCost(null)}
            />
        </div>
    );
};

// Two wrappers to keep import names simple in DossierPage
export const BudgetTab = (props) => <CostTab {...props} categorie="budget" />;
export const ChargesTab = (props) => <CostTab {...props} categorie="charges" />;
